import axios from "axios/index";
import store from "../stores/store";
import i18n from "../i18n";

class AgreementService {
  constructor() {
    this.requestHeaders = {
      Authorization: "",
      "x-api-key": "",
      Accept: "application/json",
    };
    this.localizationCache = new Map(); // Cache for loaded JSON files
  }

  getRequestHeaders() {
    const state = store.getState();
    this.requestHeaders = {
      Authorization: state.AuthReducer.userAccessToken,
      "x-api-key": 'HendrixService1',
    };

    return this.requestHeaders;
  }

  getAPIbaseUrl() {
    const state = store.getState();
    return (
      state.AppReducer.applicationConfig.support_anyware_api_url +
      "/products_support/approval_service/"
    );
  }


  viewAgreementPDF = async (agreementId,status, download = false) => {
    const agreementStatus = status === 'APPROVED' ? '?status=ACCEPTED' : '';
    const state = store.getState();
    const hendrixUrl = state.AppReducer.applicationConfig.hendrix_api_url;
    
    // Build query parameters
    const queryParams = download ? '?download=true' : '';
    const apiUrl = hendrixUrl + "/vip/customer/agreements/" + agreementId + "/viewpdf" +agreementStatus + queryParams;
    
    // Always use blob response type since server returns stream/binary data for both cases
    return await axios({
      url: apiUrl,
      method: "get",
      headers: this.getRequestHeaders(),
      responseType: 'blob',
    });
  };

  getAgreementById = async (agreementId) => {
    try {
      const state = store.getState();
      const hendrixUrl = state.AppReducer.applicationConfig.hendrix_api_url;
      const apiUrl = hendrixUrl + "/vip/customer/agreements/" + agreementId;

      const response = await axios({
        url: apiUrl,
        method: 'get',
        headers: this.getRequestHeaders(),
      });

      // Extract locale from API response
      const locale = (response.data && response.data.details && response.data.details.language) || 'en';
      i18n.changeLanguage(locale);

      // Fetch locale-specific JSON file
      const localeData = await this.fetchLocaleFile(locale, 'en');

      // Localize API response data
      const localizedApiData = this.localizeApiResponse(response.data, localeData);

      // Map the localized response data
      const mappedAgreement = this.mapSingleAgreementResponse(localizedApiData);

      if (mappedAgreement) {

        return {
          success: true,
          data: mappedAgreement,
          message: 'Agreement found successfully',
          locale: locale,
          localizationApplied: true
        };
      } else {
        return {
          success: false,
          data: null,
          message: `Failed to process agreement with ID ${agreementId}`
        };
      }

    } catch (error) {
      return {
        success: false,
        data: null,
        message: `Failed to fetch agreement: ${(error.response && error.response.data && error.response.data.message) || error.message}`
      };
    }
  };

  // Localization Methods
  
  async fetchLocaleFile(locale = 'en', fallbackLocale = 'en') {
    const cacheKey = `sales-${locale}`;
    
    // Check cache first
    if (this.localizationCache.has(cacheKey)) {
      return this.localizationCache.get(cacheKey);
    }

    try {
      
      // Fetch locale-specific JSON file
      const response = await fetch(`${process.env.PUBLIC_URL || ''}/sales-${locale}.json`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch locale file: ${response.status}`);
      }
      
      const localeData = await response.json();
      
      // Cache the result
      this.localizationCache.set(cacheKey, localeData);
      
      return localeData;
      
    } catch (error) {
      
      // Fallback to default locale
      if (locale !== fallbackLocale) {
        return this.fetchLocaleFile(fallbackLocale, fallbackLocale);
      }
      
      // If even fallback fails, return empty object
      return {};
    }
  }

  /**
   * Map API paths to JSON structure paths
   * @param {string} apiPath - API path that may need transformation
   * @returns {string} - Mapped path for JSON structure
   */
  mapApiPathToJsonPath(apiPath) {
    if (!apiPath || typeof apiPath !== 'string') {
      return apiPath;
    }

    // Handle terms path mapping - remove "contents" level from API paths
    if (apiPath.includes('NEW_ORDER.terms.')) {
      let mappedPath = apiPath.replace('NEW_ORDER.terms.', 'NEW_ORDER.terms.');
      
      // Handle specific term key mappings
      if (mappedPath === 'NEW_ORDER.terms.autoRenewalTerm') {
        mappedPath = 'NEW_ORDER.terms.autoRenewal';
      }
      
      return mappedPath;
    }

    return apiPath;
  }

  /**
   * Resolve dot-separated path in JSON object
   * @param {Object} jsonObject - The JSON object to traverse
   * @param {string} path - Dot-separated path (e.g., 'NEW_ORDER.products.billingCycleValues.totalInAdvance')
   * @param {string} defaultValue - Default value if path not found
   * @returns {string} - Resolved value or default
   */
  resolvePath(jsonObject, path, defaultValue = '', debug = false) {
    if (!path || typeof path !== 'string') {
      return defaultValue;
    }

    try {
      
      const keys = path.split('.');
      let current = jsonObject;
      
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        
        if (current && typeof current === 'object' && key in current) {
          current = current[key];
        } else {
          if (debug) {
          }
          return defaultValue || path; // Return original path if no default provided
        }
      }
      
      const result = typeof current === 'string' ? current : defaultValue || path;
      
      if (debug) {
      } else if (result && result !== path) {
      }
      
      return result;
      
    } catch (error) {
      return defaultValue || path;
    }
  }

  localizeApiResponse(apiData, localeData) {
    if (!apiData || !localeData) {
      return apiData;
    }


    // Clone the API data to avoid mutations
    const localizedData = JSON.parse(JSON.stringify(apiData));

    try {
      // Localize product-level fields
      if (localizedData.details.orderDetails && localizedData.details.orderDetails.products) {
        localizedData.details.orderDetails.products = localizedData.details.orderDetails.products.map(product => {
          const localizedProduct = { ...product };
          
          // Localize billingCycleLabel
          if (product.billingCycleLabel && typeof product.billingCycleLabel === 'string') {
            const originalValue = product.billingCycleLabel;
            const mappedKey = this.mapApiPathToJsonPath(originalValue);
            localizedProduct.billingCycleLabel = this.resolvePath(localeData, mappedKey, originalValue);
            localizedProduct._originalBillingCycleLabel = originalValue; // Keep original for reference
          }
          
          // Localize unitOfMeasure
          if (product.unitOfMeasure && typeof product.unitOfMeasure === 'string') {
            const originalValue = product.unitOfMeasure;
            const mappedKey = this.mapApiPathToJsonPath(originalValue);
            localizedProduct.unitOfMeasure = this.resolvePath(localeData, mappedKey, originalValue);
            localizedProduct._originalUnitOfMeasure = originalValue; // Keep original for reference
          }
          
          return localizedProduct;
        });
      }

      // Localize terms array
      if (localizedData.details.terms && Array.isArray(localizedData.details.terms)) {
        
        localizedData.details.terms = localizedData.details.terms.map((term, index) => {
          const localizedTerm = { ...term };
          
          if (term.termKey && typeof term.termKey === 'string') {
            const originalKey = term.termKey;
            const mappedKey = this.mapApiPathToJsonPath(originalKey);
            
            localizedTerm.localizedText = this.resolvePath(localeData, mappedKey, originalKey);
            localizedTerm._originalTermKey = originalKey; // Keep original for reference
            localizedTerm._mappedKey = mappedKey; // Keep mapped key for reference
          }
          
          return localizedTerm;
        });
        
      } else {
      }

      return localizedData;
      
    } catch (error) {
      return apiData; // Return original data if localization fails
    }
  }



  // New mapping function for comprehensive agreement response
  mapSingleAgreementResponse = (apiData) => {
    try {
      if (!apiData || (!apiData.agreementId && !apiData.id)) {
        return null;
      }


      // Extract customer information from nested structure
      const customerInfo = apiData.details.accountInfo || {};
      const customerName = customerInfo.companyName || 'Unknown Customer';
      const contractId = customerInfo.contractId || '';

      // Extract initiator/sales rep information
      const initiatorInfo = apiData.initiatorDetails || {};
      const salesRep = initiatorInfo.firstName && initiatorInfo.lastName ? 
                      `${initiatorInfo.firstName} ${initiatorInfo.lastName}` : 
                      (initiatorInfo.initiatedBy || 'Unknown');

      // Extract approver information
      const approvers = apiData.details.approvers || [];
      const approverEmails = approvers.map(approver => approver.email).join(', ');

      // Helper function to format date as DD-MMM-YYYY
      const formatDateToDDMMMYYYY = (date) => {
        const d = new Date(date);
        return d.toLocaleDateString('en-GB', {
          timeZone: 'UTC',           
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).replace(',', '').replace(/ /g, '-').toUpperCase();
      };

      // Calculate agreement expiry date
      let agreementExpiryDate = '';
      if (apiData.createDate && apiData.agreementTtl) {
        const publishDate = new Date(apiData.createDate);
        const expiryDate = new Date(publishDate.getTime() + (parseInt(apiData.agreementTtl) * 24 * 60 * 60 * 1000));
        agreementExpiryDate = formatDateToDDMMMYYYY(expiryDate);
      }

      // Extract product information from orderDetails.products
      const products = (apiData.details.orderDetails.products || []).map(product => {
        return {
          // Product identification
          sku: product.offerId || '',
          productCode: product.productCode || '',
          productConfig: product.productConfig || '',
          
          // Product description and details
          productName: product.productName || product.productFamily || 'Unknown Product',
          productDesc: product.productFamily || product.productName || 'Unknown Product',
          productFamily: product.productFamily || '',
          additionalDetails: product.additionalDetails || '',
          platform: product.platform || '',
          language: product.language || '',
          
          // Product metadata
          productType: product.productType || '',
          productIconUrl: product.productIconUrl || '',
          promoCode: product.promoCode || '',
          promoType: product.promoType || '',
          qty: product.qty ? product.qty.toString() : '0',
          unitOfMeasure: product.unitOfMeasure || '',
          
          // Billing and duration
          billingCycle: product.billingCycleLabel || '',
          duration: product.duration || '',
          
          // Pricing information
          annualUnitPrice: product.annualUnitPrice ? Number(product.annualUnitPrice).toFixed(2) : '0',
          termUnitPrice: product.termUnitPrice ? Number(product.termUnitPrice).toFixed(2) : '0',
          totalPrice: product.totalPrice ? Number(product.totalPrice).toFixed(2) : '0',
          
          // Date information
          startDate: product.startDate || '',
          endDate:  product.endDate,
          
          // Additional fields for backward compatibility
          measureUnit: product.unitOfMeasure || '',
          billing_cycle: product.billingCycleLabel || ''
        };
      });

      // Extract financial totals
      const totals = apiData.details.orderDetails.totals || {};

      // Extract payment details
      const paymentDetails = apiData.details.paymentDetails || {};

      // Use agreementId as primary identifier
      const primaryId = apiData.id;

      // Extract terms information
      const terms = apiData.details.terms || [];

      // Include document details only for approved agreements when available
      const mappedDocDetails = (apiData.status === 'APPROVED' && apiData.docDetails)
        ? apiData.docDetails
        : null;

      return {
        // Core Agreement Information
        agreementId: primaryId,
        agreementNumber: apiData.agreementNumber,
        workflowId: apiData.details.orgId || '',
        documentId: apiData.documentId || '',
        
        // Agreement Status & Type
        flowType: apiData.details.agreementType || '',
        status: apiData.status || 'DRAFT',
        agreementType: apiData.details.orderDetails.orderType || 'STANDARD',
        productType: apiData.details.orderDetails.productType || '',
        
        // Dates
        agreementPublishDate: apiData.createDate || '',
        agreementExpiryInDays: apiData.agreementTtl || '',
        agreementExpiryDate: agreementExpiryDate,
        lastModifiedDate: apiData.modifiedDate || '',
        lastModifiedBy: apiData.modifiedBy || '',
        approvedDate: apiData.approvedDate || '',
        // Customer Information
        customerName: customerName,
        contractId: contractId, // VIP Membership Number
        paNumber: apiData.paNumber || '',
        
        // Customer Account Details
        accountId: customerInfo.accountId || '',
        imsOrgId: customerInfo.imsOrgId || '',
        primaryChannel: customerInfo.primaryChannel || '',
        marketSegment: customerInfo.marketSegment || '',
        taxId: customerInfo.taxId || '',
        taxType: customerInfo.taxType || '',
        resellerId: customerInfo.resellerId || '',
        anniversaryDate: customerInfo.anniversaryDate || '',
        is3YC: customerInfo.is3YC || '',
        
        // Language & Locale
        currency: apiData.details.currency || '',
        agreementLanguage: apiData.details.language || { name: 'English', locale: 'en' },
        preferredLanguage: customerInfo.preferredLanguage || 'en-US',
        
        // People & Approval
        salesRep: salesRep,
        initiatorDetails: initiatorInfo,
        approvers: approvers,
        approverEmails: approverEmails,
        approvedBy: apiData.approvedBy || {},
        approverDetails: apiData.details.approverDetails || {},
        isApprovedByAgent: apiData.isApprovedByAgent || false,
        
        // Financial Information
        totalValue: totals.totalPrice ? totals.totalPrice.toString() : '0',
        subTotalWithoutTax: totals.subTotalWithoutTax ? Number(totals.subTotalWithoutTax).toFixed(2) : '0',
        estimatedTax: totals.estimatedTax ? Number(totals.estimatedTax).toFixed(2) : '0',
        grandTotal: totals.totalPrice ? Number(totals.totalPrice).toFixed(2) : '0',
        
        // Payment Details
        paymentMethod: paymentDetails.paymentMethod || '',
        poNumber: paymentDetails.poNumber || '',
        
        // Products
        products: products,
        
        // Agreement Features
        autoRenewal: apiData.details.orderDetails.autoRenewal || false,
        terms: terms,
        
        // Document References
        uploadedAcceptanceDocumentId: apiData.acceptanceDocId || '',
        approvedAgreementDocumentId: apiData.documentId || '',
        docDetails: JSON.parse(mappedDocDetails),
        
        // Complete nested objects for detailed access
        customerDetails: apiData.details.accountInfo || {},
        orderDetails: apiData.details.orderDetails || {},
        totals: totals,
        billingInfo: apiData.details.billingInfo || {},
        deploymentInfo: apiData.details.deploymentInfo || {},
        
        // Date information for component compatibility
        startDate: products.length > 0 ? products[0].startDate : (apiData.agreementPublishDate || ''),
        endDate: products.length > 0 ? formatDateToDDMMMYYYY(products[0].endDate) : '',
        
        // Enhanced notes with comprehensive info
        notes: [
          `Status: ${apiData.status || 'DRAFT'}`,
          `Flow Type: ${apiData.flowType || 'N/A'}`,
          `Published: ${apiData.agreementPublishDate || 'N/A'}`,
          `Modified by: ${apiData.lastModifiedBy || 'N/A'}`,
          contractId ? `VIP Contract: ${contractId}` : '',
          apiData.paNumber ? `PA Number: ${apiData.paNumber}` : '',
          paymentDetails.paymentMethod ? `Payment: ${paymentDetails.paymentMethod}` : '',
          paymentDetails.poNumber ? `PO: ${paymentDetails.poNumber}` : ''
        ].filter(Boolean).join(' | ')
      };
      
    } catch (error) {
      return null;
    }
  };
  utcToPST(utcDateTime) {
    // Parse as UTC
    const date = new Date(utcDateTime + 'Z');
  
    // Format in America/Los_Angeles timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  
    const parts = formatter.formatToParts(date);
    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  
    return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`;
  }


  approveRequest = async (agreementId) => {
    const state = store.getState();
    const hendrixUrl = state.AppReducer.applicationConfig.hendrix_api_url;
    const apiUrl = hendrixUrl + "/vip/customer/agreements/" + agreementId + "/approve";
    return await axios({
      url: apiUrl,
      method: "post",
      headers: this.getRequestHeaders(),
    });
  };

  rejectRequest = async (agreementId, reason) => {
    const state = store.getState();
    const hendrixUrl = state.AppReducer.applicationConfig.hendrix_api_url;
    const queryParams = reason ? `?comment=${encodeURIComponent(reason)}` : '';
    const apiUrl = hendrixUrl + "/vip/customer/agreements/" + agreementId + "/reject" + queryParams;
    return await axios({
      url: apiUrl,
      method: "post",
      headers: this.getRequestHeaders(),
    });
  };


  getConfig = async () => {
    return await axios({
      url: process.env.PUBLIC_URL + "/config.json",
      method: "get",
    });
  };

}

const agreementService = new AgreementService();
export default agreementService;
